import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DynamicCase } from '../../types'; // Updated from PatientCase
import type { RootState, CasesState } from '../types';
import { apiSlice } from '../api/apiSlice';

// Initial state using the CasesState interface from types.ts
const initialState: CasesState = {
  itemsByField: {},
  loading: {},
  errors: {},
};

const casesSlice = createSlice({
  name: 'cases',
  initialState,
  reducers: {
    // Set cases for a specific field
    setCasesForField: (state, action: PayloadAction<{ fieldId: string; cases: DynamicCase[] }>) => {
      const { fieldId, cases } = action.payload;
      state.itemsByField[fieldId] = cases;
    },
    
    // Clear cases for a specific field
    clearCasesForField: (state, action: PayloadAction<string>) => {
      const fieldId = action.payload;
      delete state.itemsByField[fieldId];
      delete state.loading[fieldId];
      delete state.errors[fieldId];
    },
    
    // Clear all cases (useful for logout or reset)
    clearAllCases: (state) => {
      state.itemsByField = {};
      state.loading = {};
      state.errors = {};
    },
    
    // Set loading state for a field
    setFieldLoading: (state, action: PayloadAction<{ fieldId: string; loading: boolean }>) => {
      const { fieldId, loading } = action.payload;
      state.loading[fieldId] = loading;
    },
    
    // Set error state for a field
    setFieldError: (state, action: PayloadAction<{ fieldId: string; error: string | null }>) => {
      const { fieldId, error } = action.payload;
      state.errors[fieldId] = error;
    },
    
    // Add or update a single case (useful for real-time updates)
    addOrUpdateCase: (state, action: PayloadAction<DynamicCase>) => {
      const dynamicCase = action.payload;
      const fieldId = dynamicCase.field_id;
      
      if (!state.itemsByField[fieldId]) {
        state.itemsByField[fieldId] = [];
      }
      
      const existingIndex = state.itemsByField[fieldId].findIndex(c => c.id === dynamicCase.id);
      if (existingIndex >= 0) {
        // Update existing case
        state.itemsByField[fieldId][existingIndex] = dynamicCase;
      } else {
        // Add new case
        state.itemsByField[fieldId].unshift(dynamicCase);
      }
    },
    
    // Remove a case from state
    removeCase: (state, action: PayloadAction<{ caseId: string; fieldId: string }>) => {
      const { caseId, fieldId } = action.payload;
      
      if (state.itemsByField[fieldId]) {
        state.itemsByField[fieldId] = state.itemsByField[fieldId].filter(c => c.id !== caseId);
      }
    },
    
    // Update case processing status (for LLM processing workflow)
    updateCaseProcessingStatus: (state, action: PayloadAction<{ 
      caseId: string; 
      status: DynamicCase['processing_status'] 
    }>) => {
      const { caseId, status } = action.payload;
      
      // Find and update the case across all fields
      Object.keys(state.itemsByField).forEach(fieldId => {
        const caseIndex = state.itemsByField[fieldId].findIndex(c => c.id === caseId);
        if (caseIndex !== -1) {
          state.itemsByField[fieldId][caseIndex].processing_status = status;
        }
      });
    },
  },
  
  extraReducers: (builder) => {
    // Handle RTK Query getCases endpoints
    builder
      .addMatcher(apiSlice.endpoints.getCases.matchPending, (state, action) => {
        const fieldId = action.meta.arg.originalArgs.fieldId;
        state.loading[fieldId] = true;
        state.errors[fieldId] = null;
      })
      .addMatcher(apiSlice.endpoints.getCases.matchFulfilled, (state, action) => {
        const fieldId = action.meta.arg.originalArgs.fieldId;
        const cases = action.payload; // Now DynamicCase[] instead of PatientCase[]
        
        state.loading[fieldId] = false;
        state.errors[fieldId] = null;
        state.itemsByField[fieldId] = cases;
      })
      .addMatcher(apiSlice.endpoints.getCases.matchRejected, (state, action) => {
        const fieldId = action.meta.arg.originalArgs.fieldId;
        state.loading[fieldId] = false;
        state.errors[fieldId] = action.error.message || 'Failed to load cases';
      })
      
      // Handle single case retrieval
      .addMatcher(apiSlice.endpoints.getCase.matchFulfilled, (state, action) => {
        const dynamicCase = action.payload; // Now DynamicCase instead of PatientCase
        
        // Update the case if it exists in any field
        Object.keys(state.itemsByField).forEach(fieldId => {
          const caseIndex = state.itemsByField[fieldId].findIndex(c => c.id === dynamicCase.id);
          if (caseIndex !== -1) {
            state.itemsByField[fieldId][caseIndex] = dynamicCase;
          }
        });
      })
      
      // Handle case creation
      .addMatcher(apiSlice.endpoints.createCase.matchFulfilled, (state, action) => {
        const newCase = action.payload; // DynamicCase
        const fieldId = newCase.field_id;
        
        if (!state.itemsByField[fieldId]) {
          state.itemsByField[fieldId] = [];
        }
        
        // Add new case to the beginning of the list
        state.itemsByField[fieldId].unshift(newCase);
      })
      
      // Handle case updates
      .addMatcher(apiSlice.endpoints.updateCase.matchFulfilled, (state, action) => {
        const updatedCase = action.payload; // DynamicCase
        const fieldId = updatedCase.field_id;
        
        if (state.itemsByField[fieldId]) {
          const caseIndex = state.itemsByField[fieldId].findIndex(c => c.id === updatedCase.id);
          if (caseIndex !== -1) {
            state.itemsByField[fieldId][caseIndex] = updatedCase;
          }
        }
      })
      
      // Handle case deletion
      .addMatcher(apiSlice.endpoints.deleteCase.matchFulfilled, (state, action) => {
        const caseId = action.meta.arg.originalArgs; // string
        
        // Remove from all fields
        Object.keys(state.itemsByField).forEach(fieldId => {
          state.itemsByField[fieldId] = state.itemsByField[fieldId].filter(c => c.id !== caseId);
        });
      })
      
      // Handle LLM case processing
      .addMatcher(apiSlice.endpoints.processCase.matchPending, (state, action) => {
        const { fieldId } = action.meta.arg.originalArgs;
        state.loading[fieldId] = true;
      })
      .addMatcher(apiSlice.endpoints.processCase.matchFulfilled, (state, action) => {
        const { fieldId } = action.meta.arg.originalArgs;
        state.loading[fieldId] = false;
        // Note: Actual case will be added when processing completes
      })
      .addMatcher(apiSlice.endpoints.processCase.matchRejected, (state, action) => {
        const { fieldId } = action.meta.arg.originalArgs;
        state.loading[fieldId] = false;
        state.errors[fieldId] = action.error.message || 'Failed to process case';
      })
      
      // Handle search results
      .addMatcher(apiSlice.endpoints.searchCases.matchFulfilled, (state, action) => {
        // Search results don't modify the main case storage
        // They're handled separately by the search component
      });
  },
});

// Export actions
export const { 
  setCasesForField, 
  clearCasesForField, 
  clearAllCases, 
  setFieldLoading, 
  setFieldError,
  addOrUpdateCase,
  removeCase,
  updateCaseProcessingStatus
} = casesSlice.actions;

// Export selectors with updated types
export const selectCasesForField = (state: RootState, fieldId: string): DynamicCase[] =>
  state.cases.itemsByField[fieldId] || [];

export const selectCasesLoadingForField = (state: RootState, fieldId: string): boolean =>
  state.cases.loading[fieldId] || false;

export const selectCasesErrorForField = (state: RootState, fieldId: string): string | null =>
  state.cases.errors[fieldId] || null;

export const selectCaseById = (state: RootState, caseId: string): DynamicCase | undefined => {
  for (const cases of Object.values(state.cases.itemsByField)) {
    const foundCase = cases.find(c => c.id === caseId);
    if (foundCase) return foundCase;
  }
  return undefined;
};

export const selectAllCases = (state: RootState): DynamicCase[] => {
  return Object.values(state.cases.itemsByField).flat();
};

// New selectors for processing status and difficulty
export const selectCasesByStatus = (state: RootState, status: DynamicCase['processing_status']): DynamicCase[] => {
  return Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.processing_status === status);
};

export const selectCasesByDifficulty = (state: RootState, difficulty: DynamicCase['difficulty_level']): DynamicCase[] => {
  return Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.difficulty_level === difficulty);
};

// Selector for cases needing review
export const selectCasesNeedingReview = (state: RootState): DynamicCase[] => {
  return selectCasesByStatus(state, 'review_required');
};

// Selector for completed cases
export const selectCompletedCases = (state: RootState): DynamicCase[] => {
  return selectCasesByStatus(state, 'completed');
};

// Selector for processing cases
export const selectProcessingCases = (state: RootState): DynamicCase[] => {
  return Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.processing_status === 'processing' || c.processing_status === 'pending');
};

export default casesSlice.reducer;